/**
 * pillarSeries — the pure viewmodel behind a pillar's grouped plot.
 *
 * The matrix already groups by pillar; the charts wall threw that grouping away and drew
 * one card per metric, which is why the single most important fact in Profit — Revenue and
 * Revenue Firm are the SAME quantity measured two ways, and the gap between them is the
 * part nobody has ordered yet — was invisible. Grouping is the fix; the units are the catch.
 *
 * A pillar is not a unit. Profit mixes £k with a %, Delivery mixes % with £k; the other
 * five are single-unit and plot as they are. A dual axis is never the answer (it invents a
 * correlation out of two arbitrary scales), so this offers the two honest ones:
 *
 *   absolute        one unit at a time. Series in other units are EXCLUDED, named in the
 *                   legend with the reason — faceting, not hiding.
 *   pct_of_target   every series indexed to its own target, so £k and % become
 *                   commensurable on one axis. The standard fix for mixed measures.
 *
 * Colour follows the METRIC, never its position among whatever is currently plotted:
 * `colorIndex` is fixed by the metric's place in the pillar, so excluding a series never
 * repaints the survivors. Slots are validated (see PILLAR_SERIES_HEX).
 */

import type { Cadence, MetricFormat, RegistryMetric, ResolveResponse, Site } from '../../types/metrics'
import { buildTrendSeries } from './trendSeries'
import { splitSeries } from './forecastSplit'

export type PlotMode = 'absolute' | 'pct_of_target'

/** Categorical slots, in fixed assignment order: blue → orange → cyan → purple → pink.
 *
 * GREEN AND RED ARE DELIBERATELY ABSENT. They are this product's status vocabulary — a
 * cell's RAG, the trend dots — so a green series line would read as "good" rather than
 * "Revenue". Status colour is never reused for identity.
 *
 * Both columns are validated as a set with the data-viz validator, on the surface each is
 * drawn against (light #ffffff / dark #111827), on the adjacent pairlist a line chart uses:
 *   light  worst adjacent CVD ΔE 12.2 (protan) · normal-vision 19.9 · all ≥ 3:1 contrast
 *   dark   worst adjacent CVD ΔE 14.4 (deutan) · normal-vision 20.6 · all ≥ 3:1 contrast
 * The dark steps for orange/cyan/pink are darker than the corresponding --orange/--cyan/
 * --pink tokens on purpose: the raw tokens sit above the dark lightness band (L .77/.80/.73
 * against a .48–.67 ceiling). Re-run the validator before changing any value here.
 * Five slots is the ceiling AND the requirement — Profit, the largest pillar, has five.
 */
export const PILLAR_SERIES_HEX: Record<'light' | 'dark', readonly string[]> = {
  light: ['#2f6fe0', '#e07a10', '#0a97ad', '#7c4ddc', '#d6489a'],
  dark: ['#4b8bf5', '#d97706', '#0e9fb8', '#8b5cf6', '#db4f96'],
}

export interface PillarSeriesModel {
  metricId: string
  title: string
  format: MetricFormat
  colorIndex: number // fixed by position in the pillar — survives any exclusion
  plotted: boolean
  /** Why this series is off the plot, in words for the legend. null when plotted. */
  excluded: string | null
  hasTarget: boolean
}

export interface PillarPlotModel {
  mode: PlotMode
  /** The unit being plotted in absolute mode; null in pct_of_target (all one scale). */
  unit: MetricFormat | null
  /** Distinct units across the pillar, in first-appearance order — the unit switcher. */
  units: MetricFormat[]
  series: PillarSeriesModel[]
  /** Recharts rows: { period, label, [metricId]: number | null }. */
  rows: Array<Record<string, string | number | null>>
  plottedCount: number
}

const UNIT_WORDS: Record<MetricFormat, string> = {
  gbp_k: '£k',
  pct: '%',
  days: 'days',
  count: 'count',
}

export function unitLabel(format: MetricFormat): string {
  return UNIT_WORDS[format] ?? String(format)
}

/** Matrix metrics of one pillar at this cadence, in catalogue order. */
export function pillarMetrics(metrics: RegistryMetric[], cadence: Cadence): RegistryMetric[] {
  return metrics.filter((m) => m.matrix && m.cadence.includes(cadence))
}

/** Distinct units in first-appearance order. A single entry means no unit switcher. */
export function pillarUnits(metrics: RegistryMetric[]): MetricFormat[] {
  return [...new Set(metrics.map((m) => m.format))]
}

/** Mixed-unit pillars open on % of target — the only mode that shows all their series at
 *  once. Single-unit pillars open on absolute, where the real magnitudes are readable. */
export function defaultMode(metrics: RegistryMetric[]): PlotMode {
  return pillarUnits(metrics).length > 1 ? 'pct_of_target' : 'absolute'
}

function ratioToTarget(actual: number | null, target: number | null): number | null {
  if (actual === null || target === null || target <= 0) return null
  return Math.round((actual / target) * 1000) / 10
}

/** A target at or below zero cannot be indexed against. EBITDA plans a LOSS in some
 *  months (Kirkstall Jan 2026 target −£68,190), and £24,361 against that reads as
 *  "−36% of target" — the sign inverts and the magnitude is meaningless, when what
 *  actually happened is they beat the plan by £92k. Near-zero positives are kept: a
 *  £1,140 target genuinely beaten fourfold IS 425%, which is true and worth seeing. */
function targetCrossesZero(targets: Array<number | null>): boolean {
  return targets.some((target) => target !== null && target <= 0)
}

interface BuiltSeries {
  metric: RegistryMetric
  index: number
  series: ReturnType<typeof buildTrendSeries>
}

/** One series' place in this view: plotted, or excluded WITH the reason in words. */
function describeSeries(
  { metric, index, series: trend }: BuiltSeries,
  mode: PlotMode,
  activeUnit: MetricFormat | null
): PillarSeriesModel {
  const targets = trend.points.map((point) => point.target)
  const hasTarget = targets.some((target) => target !== null)
  const wrongUnit = mode === 'absolute' && metric.format !== activeUnit
  const untargeted = mode === 'pct_of_target' && !hasTarget
  const unindexable = mode === 'pct_of_target' && hasTarget && targetCrossesZero(targets)
  return {
    metricId: metric.id,
    title: metric.title,
    format: metric.format,
    colorIndex: index,
    plotted: !wrongUnit && !untargeted && !unindexable,
    excluded: wrongUnit
      ? `in ${unitLabel(metric.format)}`
      : untargeted
        ? 'no target set'
        : unindexable
          ? 'target crosses zero'
          : null,
    hasTarget,
  }
}

/** Recharts rows. An excluded series contributes NO key, so nothing can reach the axis.
 *  Each plotted series is written through splitSeries, which adds the measured/forecast
 *  keys the chart needs to draw one line solid and its forward run dashed. */
function toRows(
  periods: string[],
  built: BuiltSeries[],
  plottedIds: Set<string>,
  mode: PlotMode
): Array<Record<string, string | number | null>> {
  const rows = periods.map((period, periodIndex) => ({
    period,
    label: built[0]?.series.points[periodIndex]?.label ?? period,
  })) as Array<Record<string, string | number | null>>
  for (const { metric, series: trend } of built) {
    if (!plottedIds.has(metric.id)) continue
    const values = trend.points.map((point) =>
      mode === 'pct_of_target' ? ratioToTarget(point.actual, point.target) : point.actual
    )
    splitSeries(rows, metric.id, values, trend.points.map((point) => point.forecast))
  }
  return rows
}

export function buildPillarPlot(
  resolve: ResolveResponse,
  metrics: RegistryMetric[],
  site: Site,
  mode: PlotMode,
  unit: MetricFormat | null
): PillarPlotModel {
  const units = pillarUnits(metrics)
  const activeUnit = mode === 'absolute' ? (unit && units.includes(unit) ? unit : units[0]) : null
  const built = metrics.map((metric, index) => ({
    metric,
    index,
    series: buildTrendSeries(resolve, metric.id, site, metric.title),
  }))

  const series = built.map((entry) => describeSeries(entry, mode, activeUnit))
  const plottedIds = new Set(series.filter((s) => s.plotted).map((s) => s.metricId))
  const rows = toRows(resolve.periods, built, plottedIds, mode)
  return { mode, unit: activeUnit, units, series, rows, plottedCount: plottedIds.size }
}
